// Role vocabulary + capability helpers + route access table.
//
// Mirrors 2Back/app/core/deps.py (require_* groups) and the users.role CHECK
// (migration 0040). Keep the three in sync.
//
//   customer    — own data only (CardCode-scoped)
//   accountant  — operational: OCR pipeline + collections + print-billing
//   admin       — governance: /admin/*, /system/*, data corrections
//   management  — executive view: OCR overview + collections dashboards
//                 (READ-ONLY) + งานติดตามหนี้ executive pages
//   collection  — collection officer: collections + งานติดตามหนี้ queues with
//                 full read/write; NO OCR pipeline access
//   ocr         — OCR operator: the accountant's OCR half only (upload /
//                 jobs / link / print-billing + SAP document browsers).
//                 NO collections, NO user management, NO ?as_customer.

import type { Role } from "./types";

export const ALL_ROLES: Role[] = ["customer", "accountant", "admin", "management", "collection", "ocr"];

export const ROLE_LABELS: Record<Role, string> = {
  customer: "Customer",
  accountant: "Accountant",
  admin: "Admin",
  management: "Management",
  collection: "Collection",
  ocr: "OCR",
};

export const ROLE_DESCRIPTIONS: Record<Role, string> = {
  customer: "ลูกค้า — ดูข้อมูลของตัวเองตาม CardCode",
  accountant: "บัญชี — งาน OCR เอกสาร + ติดตามหนี้ + พิมพ์วางบิล",
  admin: "ผู้ดูแลระบบ — จัดการผู้ใช้ / นำเข้า CSV / แก้ไขข้อมูล",
  management: "ผู้บริหาร — ดูภาพรวม OCR และ Collections (อ่านอย่างเดียว)",
  collection: "เจ้าหน้าที่ติดตามหนี้ — Collections + งานติดตามหนี้",
  ocr: "เจ้าหน้าที่ OCR — งาน OCR เอกสาร + พิมพ์วางบิล + ข้อมูล SAP (ไม่มี Collections)",
};

/** Landing page after login / when visiting a public page while signed in. */
export function homePathFor(role: Role | undefined | null): string {
  switch (role) {
    case "admin":       return "/admin/dashboard";
    case "accountant":  return "/accountant/dashboard";
    case "management":  return "/collections/executive";
    case "collection":  return "/collections/today";
    case "ocr":         return "/accountant/invoice-images";
    default:            return "/dashboard";
  }
}

/** May see the Collections (ติดตามหนี้) + งานติดตามหนี้ sections at all. */
export function canReadCollections(role: Role | undefined | null): boolean {
  return role === "accountant" || role === "admin" || role === "management" || role === "collection";
}

/** May log follow-ups, set statuses, create/edit installment plans, update ECL rates. */
export function canWriteCollections(role: Role | undefined | null): boolean {
  return role === "accountant" || role === "admin" || role === "collection";
}

/** May open the OCR pages (dashboards, image lists/details). */
export function canReadOcr(role: Role | undefined | null): boolean {
  return role === "accountant" || role === "admin" || role === "management" || role === "ocr";
}

/** May upload, link/unlink images, run jobs — the operational OCR pipeline. */
export function canOperateOcr(role: Role | undefined | null): boolean {
  return role === "accountant" || role === "ocr";
}

/** May open พิมพ์วางบิล (BE: require_print_billing). */
export function canPrintBilling(role: Role | undefined | null): boolean {
  return role === "accountant" || role === "admin" || role === "ocr";
}

/** Roles whose Topbar search should resolve to a customer's AR detail. */
export function isCollectionsRole(role: Role | undefined | null): boolean {
  return role === "management" || role === "collection";
}

/** Crumb root shown before the section name, e.g. "FinApp / Accountant / …". */
export function crumbRootFor(role: Role | undefined | null): string {
  switch (role) {
    case "admin":       return "Admin";
    case "management":  return "Management";
    case "collection":  return "Collections";
    case "customer":    return "Customer";
    case "ocr":         return "OCR";
    default:            return "Accountant";
  }
}

// ---------------------------------------------------------------------------
// Route access table — enforced by AuthProvider (lib/auth.tsx) on every
// navigation. A path is allowed when it equals an entry or sits under it
// ("/accountant/invoices" covers "/accountant/invoices/LIV123"). The API is
// still the real guard (every page's data call 403s for the wrong role);
// this stops a hand-edited URL from even mounting a page outside the role's
// menu, so a user can't see another role's shell or trigger its requests.
// Keep in sync with the NAV table in components/Frame.tsx and the
// per-role `require_*` groups in 2Back/app/core/deps.py.
// ---------------------------------------------------------------------------
const CUSTOMER_PATHS = ["/dashboard", "/invoices", "/credit-notes", "/ar", "/help"];

const OCR_PATHS = [
  "/accountant/dashboard",
  "/accountant/invoice-images",
  "/accountant/credit-notes-images",
  "/accountant/jobs",
  "/accountant/print-billing",
  "/accountant/invoices",
  "/accountant/creditnotes",
  "/accountant/reserve-invoices",
  "/accountant/help",
];

export const ROUTE_ACCESS: Record<Role, readonly string[]> = {
  customer: CUSTOMER_PATHS,
  // Accountant reaches the customer pages through View-as (ViewAsBanner).
  accountant: ["/accountant", "/collections", ...CUSTOMER_PATHS],
  // Admin reuses the accountant collections URLs + can open OCR detail pages
  // from /admin/ocr-inspection; no customer pages (no ?as_customer for admin).
  admin: ["/admin", "/accountant", "/collections"],
  management: [
    "/accountant/dashboard",
    "/accountant/invoice-images",
    "/accountant/credit-notes-images",
    "/accountant/collections",
    "/collections",
  ],
  collection: ["/accountant/collections", "/collections"],
  ocr: OCR_PATHS,
};

/** True when `role` may open `pathname` (query string ignored). "/" is
 * always allowed — the root page only redirects to homePathFor(). */
export function canAccessPath(role: Role | undefined | null, pathname: string | null | undefined): boolean {
  if (!role) return false;
  const path = (pathname || "/").split("?")[0].replace(/\/+$/, "") || "/";
  if (path === "/") return true;
  const allowed = ROUTE_ACCESS[role] ?? [];
  return allowed.some((p) => path === p || path.startsWith(p + "/"));
}
